import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import StickyEmergencyBar from "@/components/support/StickyEmergencyBar";
import { VIDEOS } from "@/lib/supportContent";
import { ChevronLeft, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SupportAcademy = () => {
  const [active, setActive] = useState<string | null>(null);
  const groups = useMemo(() => {
    const robotics = VIDEOS.filter((v) => v.category === "Robotics University");
    const ai = VIDEOS.filter((v) => v.category === "Neil AI University");
    return { robotics, ai };
  }, []);

  const renderGrid = (vids: typeof VIDEOS) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {vids.map((v) => (
        <Card key={v.id} className="bg-[#0A1828]/60 border-primary/20 hover:border-primary/50 transition-all overflow-hidden cursor-pointer" onClick={() => setActive(v.id)}>
          <div className="aspect-video bg-[#05070A] flex items-center justify-center relative group">
            <PlayCircle className="w-16 h-16 text-primary/70 group-hover:text-[#00F5A0] group-hover:scale-110 transition-all" />
            <span className="absolute top-2 right-2 text-xs bg-black/70 px-2 py-1 rounded">{v.duration}</span>
          </div>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wider text-primary mb-1">{v.series}</div>
            <h3 className="font-semibold">{v.title}</h3>
          </div>
        </Card>
      ))}
    </div>
  );

  const activeVid = VIDEOS.find((v) => v.id === active);

  return (
    <>
      <SEO title="Video Training Academy | Neil AI Support" description="Robotics University and Neil AI University — on-demand training for every robot and automation we support." />
      <div className="min-h-screen bg-[#05070A] text-white pb-32">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 pt-32 md:pt-40">
          <Button asChild variant="ghost" className="mb-6 text-muted-foreground hover:text-white">
            <Link to="/support"><ChevronLeft className="w-4 h-4 mr-1" />Back to Support</Link>
          </Button>
          <div className="mb-10 text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] bg-clip-text text-transparent">Video Training Academy</h1>
            <p className="text-muted-foreground">On-demand training for every robot and AI tool we deploy.</p>
          </div>

          <Tabs defaultValue="robotics" className="w-full">
            <TabsList className="mx-auto mb-8 bg-[#0A1828]/60">
              <TabsTrigger value="robotics">Robotics University</TabsTrigger>
              <TabsTrigger value="ai">Neil AI University</TabsTrigger>
            </TabsList>
            <TabsContent value="robotics">{renderGrid(groups.robotics)}</TabsContent>
            <TabsContent value="ai">{renderGrid(groups.ai)}</TabsContent>
          </Tabs>

          {activeVid && (
            <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setActive(null)}>
              <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
                <iframe src={activeVid.url} title={activeVid.title} className="w-full h-full rounded-xl" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}
        </main>
        <Footer />
        <StickyEmergencyBar />
      </div>
    </>
  );
};

export default SupportAcademy;
